import { Alert, Button } from "@mui/material";
import { HoldStrip } from "@/components/common/HoldStrip";
import { ResourceName, type Claimant } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";

interface PendingFamilyUnitBannerProps {
  claimant?: Claimant;
  onFormFamilyUnit: () => void;
  /** `strip`: the in-card hold strip for the claimant position card (Feature #7659). */
  variant?: "alert" | "strip";
}

/** The banner's own show rule, exported so the page can know a hold exists without rendering it. */
export const needsFamilyUnit = (claimant?: Claimant): boolean => {
  if (!claimant) return false;
  const hasSubscriberLink = !!claimant.subscriber_ref_id;
  const onFamilyDedPolicy = claimant.policies?.some((p) => p.has_family_deductible_endorsement) ?? false;
  const isSelf = claimant.claimant_relationship === "self";
  return !hasSubscriberLink && onFamilyDedPolicy && !isSelf;
};

/**
 * Alerts the reviewer when the viewed claimant sits on a family-deductible
 * policy but isn't in a family unit yet. The load pipeline holds these
 * claimants — no stop-loss claim is created — until the reviewer forms a
 * family unit from here. Renders nothing when:
 *   - The claimant is already in a family (subscriber_ref_id set)
 *   - The policy has no family deductible
 *   - The claimant is a self/EE (their own deductible unit — no family
 *     required until dependents are added)
 */
export const PendingFamilyUnitBanner = ({
  claimant,
  onFormFamilyUnit,
  variant = "alert",
}: PendingFamilyUnitBannerProps) => {
  // Read-only roles see the hold and its reason, not the action (#7660 AC4).
  const { canEdit } = useResourcePermissions(ResourceName.CLAIMANTS);
  if (!needsFamilyUnit(claimant)) return null;

  if (variant === "strip") {
    return (
      <HoldStrip
        testId="pending-family-unit-banner"
        title="On hold: no family unit yet"
        reason="This policy has a family deductible and this claimant is not in a family unit yet. No claims are created until one is formed."
        action={canEdit ? { label: "Form family unit", onClick: onFormFamilyUnit } : undefined}
      />
    );
  }

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2, "& .MuiAlert-message": { fontSize: "0.8125rem", width: "100%" } }}
      action={
        <Button
          size="small"
          color="warning"
          variant="outlined"
          onClick={onFormFamilyUnit}
          sx={{ whiteSpace: "nowrap" }}
        >
          Form family unit
        </Button>
      }
    >
      This claimant is on a family-deductible policy but isn't tied to a family unit yet. Stop-loss processing is held
      until a family unit is formed — no claims will be created against their YTD in this state.
    </Alert>
  );
};
